import React, { Suspense } from "react";
import "./App.css";
// Global responsive layer — must load after App.css so its overrides win.
import "./css/responsive.css";
// User-selected typeface — last, so it outranks the font rules above.
import "./css/appFont.css";
import { ToastContainer } from "react-toastify";
import { Route, Switch, Redirect } from "react-router-dom";
import NetworkDetector from "./components/HigherOrderComponents/NetworkDetector";
import SafeRoute from "./components/HigherOrderComponents/UserProtectedRoutes";
import AdminSafeRoute from "./components/HigherOrderComponents/AdminProtectedRoutes";
import IdleSessionGuard from "./components/IdleSessionGuard";

// ─── Lazy-loaded routes ───────────────────────────────────────────────────────
const LandingPageMain    = React.lazy(() => import("./components/Landingpage/LandingPageMain"));
const Home               = React.lazy(() => import("./components/Home"));
const DashBoard          = React.lazy(() => import("./components/pages/DashBoardPage"));
const AlertsPage         = React.lazy(() => import("./components/pages/AlertsPage"));
const DocumentPreviewPage= React.lazy(() => import("./components/pages/DocumentPreviewPage"));
const TeamMembersPage    = React.lazy(() => import("./components/pages/TeamMembersPage"));
const PricingPage        = React.lazy(() => import("./components/pages/PricingPage"));
const FAQPage            = React.lazy(() => import("./components/pages/FAQPage"));
const MyDocumentsPage    = React.lazy(() => import("./components/pages/MyDocumentsPage"));
const ViewMyDocumentsPage= React.lazy(() => import("./components/pages/ViewMyDocumentsPage"));
const MyDocumentsNewProjectPage = React.lazy(() => import("./components/pages/MyDocumentsNewProjectPage"));
const AccountSettingsPage= React.lazy(() => import("./components/pages/AccountSettingsPage2"));
const SuperAdminAccountSettingsPage = React.lazy(() => import("./components/pages/SuperAdminAccountSettingsPage"));
const AdminDashBoard     = React.lazy(() => import("./components/pages/AdminDashboardPage"));
const UserInvoice        = React.lazy(() => import("./components/pages/UserInvoice"));
const ManageUserPage     = React.lazy(() => import("./components/pages/ManageUserPage"));
const ManageOrganizationPage = React.lazy(() => import("./components/pages/ManageOrganizationPage"));
const PaymentPage        = React.lazy(() => import("./components/pages/PaymentPage"));
const ReviewRatingPage   = React.lazy(() => import("./components/pages/ReviewRatingPage"));
const NewsletterPage     = React.lazy(() => import("./components/pages/NewsletterPage"));
const ManageInvoicesPage = React.lazy(() => import("./components/pages/ManageInvoicePage"));
const ResetPassPage      = React.lazy(() => import("./components/pages/ResetPassPage"));
const Logout             = React.lazy(() => import("./components/Logout"));
const PrivacyPolicy      = React.lazy(() => import("./components/PrivacyPolicy"));
const Disclaimer         = React.lazy(() => import("./components/Disclaimer"));
const TermsofService     = React.lazy(() => import("./components/TermsofService"));
const Reports            = React.lazy(() => import("./components/pages/ReportsPage"));
const DocTags            = React.lazy(() => import("./components/pages/DocTagsPage"));
const TeamCollaborationPage = React.lazy(() => import("./components/pages/TeamCollaborationPage"));
const PriceDetails       = React.lazy(() => import("./components/pages/PriceDetails"));
const PaymentReport      = React.lazy(() => import("./components/PaymentReport"));
const SubAdminManagerUserPage    = React.lazy(() => import("./components/pages/SubAdminSideBarPage"));
const SubAdminUserManagementPage = React.lazy(() => import("./components/pages/SubAdminUserManagementPage"));
const SubAdminRolesPage          = React.lazy(() => import("./components/pages/SubAdminRolesPage"));
const SubAdminPermissionsPage    = React.lazy(() => import("./components/pages/SubAdminPermissionsPage"));
const CitationPage       = React.lazy(() => import("./components/pages/CitationPage"));
const CoAuthorPage       = React.lazy(() => import("./components/pages/CoAuthorPage"));
const CoAuthorNewProjectPage = React.lazy(() => import("./components/pages/CoAuthorNewProjectPage"));
const BlogAIBusinessTool = React.lazy(() => import("./components/BlogsAIBusinessTools"));
const BusinessHub = React.lazy(() => import("./components/BusinessHub/BusinessHub"));
const BusinessRecords = React.lazy(() => import("./components/BusinessHub/BusinessRecords"));
const LogoGenerator = React.lazy(() => import("./components/BusinessHub/LogoGenerator"));
const BlogAIBookWritingTool  = React.lazy(() => import("./components/BlogAIBookWritingTool"));
const BlogAIAdvertisingTool  = React.lazy(() => import("./components/BlogAIAdvertisingTool"));
const CompareDocumentPage= React.lazy(() => import("./components/pages/CompareDocumentPage"));
const ParaphrasePage     = React.lazy(() => import("./components/pages/ParaphrasePage"));
const PlagiarismPage     = React.lazy(() => import("./components/pages/PlagiarismPage"));
const TranslatorPage     = React.lazy(() => import("./components/pages/TranslatorPage"));
const HomePriceDetails   = React.lazy(() => import("./components/HomePriceDetails"));
const MyCitationsList    = React.lazy(() => import("./components/pages/MyCitationListPage"));
const BlogSection        = React.lazy(() => import("./components/pages/BlogSectionPage"));
const AfterRegistration  = React.lazy(() => import("./components/pages/AfterRegistrationPage"));
const SummeriserPage     = React.lazy(() => import("./components/Forms/SummeriserPage"));
const Introduction       = React.lazy(() => import("./components/CostumizeLandingPage/Introduction"));
const BlogAccessForPublic= React.lazy(() => import("./components/pages/BlogAccessForPublicPage"));
const AdminBlog          = React.lazy(() => import("./components/pages/AdminBlogPage"));
const CitationProjPage   = React.lazy(() => import("./components/pages/CitationProjPage"));
const BlogsonLandingPage = React.lazy(() => import("./components/pages/BlogsonLandingpage"));
const CostumHomePage     = React.lazy(() => import("./components/pages/CostumHomePage"));
const NewHome            = React.lazy(() => import("./components/NewHome"));
const SocialMediaAuth    = React.lazy(() => import("./components/pages/SocialMediaAuthPage"));
const ParaTwoPage        = React.lazy(() => import("./components/pages/ParaTwoPage"));
const ContactUs          = React.lazy(() => import("./components/pages/ContactUsPages"));
const BlogManagementPage = React.lazy(() => import("./components/pages/BlogManagementPage"));
const LoginForm2         = React.lazy(() => import("./components/LoginForm2"));
const RegisterForm2      = React.lazy(() => import("./components/RegisterForm2"));
const ForgotPasswordForm2= React.lazy(() => import("./components/ForgotPasswordForm2"));
const OTPVerification    = React.lazy(() => import("./components/OTPVerification"));
const SubAdminDashboardPage      = React.lazy(() => import("./components/pages/SubAdminDashboardPage"));
const GrammarCheckPage           = React.lazy(() => import("./components/pages/GrammarCheckPage"));
const SubAdminUserRequestPage    = React.lazy(() => import("./components/pages/SubAdminUserRequestPage"));
const PromptsGeneratorPage       = React.lazy(() => import("./components/pages/PromptsGeneratorPage"));
const SubAdminWorkFlowPage       = React.lazy(() => import("./components/pages/SubAdminWorkFlowPage"));
const SubAdminBlogPage           = React.lazy(() => import("./components/pages/SubAdminBlogPage"));
const SubAdminDocumentsPage      = React.lazy(() => import("./components/pages/SubAdminDocumentsPage"));
const SubAdminSharedFilesPage    = React.lazy(() => import("./components/pages/SubAdminSharedFilesPage"));
const SubAdminOrganizationPage   = React.lazy(() => import("./components/pages/SubAdminOrganizationPage"));
const SubAdminTeamsPage          = React.lazy(() => import("./components/pages/SubAdminTeamsPage"));
const SubAdminTeamDetailPage     = React.lazy(() => import("./components/pages/SubAdminTeamDetailPage"));
const SubAdminApprovalsPage      = React.lazy(() => import("./components/pages/SubAdminApprovalsPage"));
const SubAdminAccountSettingsPage= React.lazy(() => import("./components/pages/SubAdminAccountSettingsPage"));
const ChatBoxPage        = React.lazy(() => import("./components/pages/ChatBoxPage"));
const GroupCallLauncher  = React.lazy(() => import("./components/ChatBox/GroupCall/GroupCallLauncher"));
const GroupCallRoom      = React.lazy(() => import("./components/ChatBox/GroupCall/GroupCallRoom"));
const MeetingsCalendarPage = React.lazy(() => import("./components/ChatBox/GroupCall/MeetingsCalendarPage"));
const SynthiumSheet       = React.lazy(() => import("./components/Sheet/SynthiumSheet"));
const GrammarCheckTrialPage      = React.lazy(() => import("./components/pages/GrammarCheckTrialPage"));
const CitationTrialPage          = React.lazy(() => import("./components/pages/CitationTrialPage"));
const PlagiarismTrialPage        = React.lazy(() => import("./components/pages/PlagiarismTrialPage"));
const CoAuthorNewProjectTrialPage= React.lazy(() => import("./components/pages/CoAuthorNewProjectTrialPage"));
const ParaphraserTrialPage       = React.lazy(() => import("./components/pages/ParaphraserTrialPage"));
const UserLogsPage       = React.lazy(() => import("./components/pages/UserLogsPage"));
const AdminUserLogPage   = React.lazy(() => import("./components/pages/AdminUserLogPage"));
const SubscriptionPage   = React.lazy(() => import("./components/pages/SubscriptionPage"));
const AddSubscriptions   = React.lazy(() => import("./components/pages/Addsubcription"));

// ─── Inline page loader — no external deps ───────────────────────────────────
const PageLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#fff" }}>
    <div style={{ textAlign: "center" }}>
      <div style={{
        width: 44, height: 44, margin: "0 auto 14px",
        border: "3px solid #e8eaf6", borderTop: "3px solid #4A90E2",
        borderRadius: "50%", animation: "app-spin 0.75s linear infinite",
      }} />
      <p style={{ color: "#9e9e9e", fontSize: 13, fontFamily: "sans-serif", margin: 0 }}>Loading…</p>
    </div>
    <style>{`@keyframes app-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  return (
    <React.Fragment>
      <ToastContainer />
      {/* Ends a session after five hours of inactivity — and only inactivity. */}
      <IdleSessionGuard />
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={LandingPageMain} exact />
          <Route path="/Summarizer" component={SummeriserPage} />
          <Route path="/blogeditor" component={BlogManagementPage} />
          <Route exact path="/login" component={LoginForm2} />
          <Route path="/otpverification" component={OTPVerification} />
          <Route path="/login/:loginParam" component={LoginForm2} />
          <Route path="/forgotpassword" component={ForgotPasswordForm2} />
          <Route path="/resetpassword" component={ResetPassPage} />
          <Route path="/signup" component={RegisterForm2} />
          <Route path="/pricing" component={PricingPage} />
          <Route path="/faq" component={FAQPage} />
          <SafeRoute path="/collaboration" component={TeamCollaborationPage} />
          <SafeRoute path="/compareDocument" component={CompareDocumentPage} />
          <AdminSafeRoute path="/manageinvoices" component={ManageInvoicesPage} />
          <Route path="/logout" component={Logout} />
          <Route path="/socialMediaAuth" component={SocialMediaAuth} />
          <SafeRoute path="/invoices" component={UserInvoice} />
          <SafeRoute path="/pricedetails" component={PriceDetails} />
          <AdminSafeRoute path="/adminaccountsettings" component={SuperAdminAccountSettingsPage} />
          <SafeRoute path="/paraphrase" component={ParaphrasePage} />
          <SafeRoute path="/paratwo" component={ParaTwoPage} />
          <SafeRoute path="/plagiarism" component={PlagiarismPage} />
          <SafeRoute path="/translator" component={TranslatorPage} />
          <AdminSafeRoute path="/admindashboard" component={AdminDashBoard} />
          <AdminSafeRoute path="/addsubscription" component={NetworkDetector(AddSubscriptions)} />
          <AdminSafeRoute path="/manageuser" component={ManageUserPage} />
          <AdminSafeRoute path="/manageorganizations" component={ManageOrganizationPage} />
          <SafeRoute path="/accountsettings" component={AccountSettingsPage} />
          <AdminSafeRoute path="/Newsletter" component={NewsletterPage} />
          <SafeRoute path="/mydocuments" component={MyDocumentsPage} />
          <SafeRoute path="/viewmydocuments/:documentId" component={ViewMyDocumentsPage} />
          <SafeRoute path="/mynewdocuments" component={MyDocumentsNewProjectPage} />
          <SafeRoute path="/grammarcheck" component={GrammarCheckPage} />
          <AdminSafeRoute path="/managereviews" component={ReviewRatingPage} />
          <AdminSafeRoute path="/adminBlogs" component={AdminBlog} />
          <AdminSafeRoute path="/promptsGenerator" component={PromptsGeneratorPage} />
          <Route path="/grammarchecktrial" component={GrammarCheckTrialPage} />
          <Route path="/citationtrial" component={CitationTrialPage} />
          <Route path="/plagiarismtrial" component={PlagiarismTrialPage} />
          <Route path="/coauthortrial" component={CoAuthorNewProjectTrialPage} />
          <Route path="/paraphrasetrial" component={ParaphraserTrialPage} />
          <Route path="/privacypolicy" component={PrivacyPolicy} />
          <Route path="/disclaimer" component={Disclaimer} />
          <Route path="/termsofservice" component={TermsofService} />
          <SafeRoute key="documentpreview" exact path="/documentpreview/:id" component={DocumentPreviewPage} />
          <SafeRoute key="blogpreview" exact path="/blogpreview/:id" component={DocumentPreviewPage} />
          <SafeRoute path="/subAdminDashboard" component={SubAdminDashboardPage} />
          <SafeRoute path="/subAdminUserManagement" component={SubAdminUserManagementPage} />
          <SafeRoute path="/subAdminRoles" component={SubAdminRolesPage} />
          <SafeRoute path="/subAdminPermissions" component={SubAdminPermissionsPage} />
          <SafeRoute path="/subAdminManageUser" component={SubAdminManagerUserPage} />
          <SafeRoute path="/subAdminDocuments" component={SubAdminDocumentsPage} />
          <SafeRoute path="/subAdminSharedFiles" component={SubAdminSharedFilesPage} />
          <SafeRoute path="/subAdminOrganizations" component={SubAdminOrganizationPage} />
          <SafeRoute path="/subAdminTeams/:id" component={SubAdminTeamDetailPage} />
          <SafeRoute path="/subAdminTeams" component={SubAdminTeamsPage} />
          <SafeRoute path="/subAdminUserRequest" component={SubAdminUserRequestPage} />
          <SafeRoute path="/subAdminApprovals" component={SubAdminApprovalsPage} />
          <SafeRoute path="/subAdminAccountSettings" component={SubAdminAccountSettingsPage} />
          <SafeRoute path="/userLogs" component={UserLogsPage} />
          <SafeRoute path="/adminUserLogs" component={AdminUserLogPage} />
          <SafeRoute path="/manageOrganizationBlogs" component={SubAdminBlogPage} />
          <SafeRoute path="/subAdminWorkFlow" component={SubAdminWorkFlowPage} />
          <SafeRoute path="/subscription" component={SubscriptionPage} />
          <SafeRoute exact path="/payment" component={PaymentPage} />
          <Route path="/homepricedetails" component={HomePriceDetails} />
          <SafeRoute path="/dashboard" component={DashBoard} />
          <SafeRoute path="/alerts" component={AlertsPage} />
          <SafeRoute path="/paymentreport" component={PaymentReport} />
          <SafeRoute path="/myteam" component={TeamMembersPage} />
          <SafeRoute path="/chat" component={ChatBoxPage} />
          <SafeRoute exact path="/groupcall" component={GroupCallLauncher} />
          <SafeRoute exact path="/groupcall/:roomName" component={GroupCallRoom} />
          <SafeRoute exact path="/meetings" component={MeetingsCalendarPage} />
          <SafeRoute exact path="/synthiumsheet" component={SynthiumSheet} />
          <SafeRoute path="/reports" component={Reports} />
          <SafeRoute path="/docTags" component={DocTags} />
          <SafeRoute path="/blogpage" component={BlogSection} />
          <SafeRoute path="/business-hub" component={BusinessHub} />
          <SafeRoute path="/logo-generator" component={LogoGenerator} />
          <SafeRoute path="/business-records" component={BusinessRecords} />
          <SafeRoute path="/business-tools" component={BlogAIBusinessTool} />
          <SafeRoute path="/book-writing" component={BlogAIBookWritingTool} />
          <SafeRoute path="/advertising-tool" component={BlogAIAdvertisingTool} />
          <SafeRoute path="/citation" component={CitationPage} />
          <SafeRoute path="/coauthor" component={CoAuthorPage} />
          <SafeRoute path="/coauthor-newproject" component={CoAuthorNewProjectPage} />
          <SafeRoute path="/citationList" component={MyCitationsList} />
          <SafeRoute path="/citationproj" component={CitationProjPage} />
          <SafeRoute path="/contactUs" component={ContactUs} />
          <AdminSafeRoute path="/introduction" component={Introduction} />
          <Route path="/public/blogpreview/:id" component={BlogAccessForPublic} />
          <Route path="/blog" component={BlogsonLandingPage} />
          <Route path="/afterRegistration" component={AfterRegistration} />
          <AdminSafeRoute path="/costumHome" component={CostumHomePage} />
          <Route path="/newhome" component={NewHome} />
          <Redirect to="/" />
        </Switch>
      </Suspense>
    </React.Fragment>
  );
}

export default App;
